
import React, { useState, useEffect } from 'react';
import SectionSelector from './SectionSelector';
import FormInput from './components/FormInput';
import FormTextarea from './components/FormTextarea';
import AddButton from './components/AddButton';
import RemoveButton from './components/RemoveButton';
import WebsiteLinksInput from './components/WebsiteLinksInput';
import CategorizedSkillsInput from './components/CategorizedSkillsInput';
import ExperiencesInput from './components/ExperiencesInput';

const TechResumeForm = ({ resumeData, onUpdate, validationErrors = {}, onFieldFocus, onFieldBlur, getDynamicSections, renderDynamicSectionForm }) => {
  const [activeSection, setActiveSection] = useState('contact');
  const [newSkill, setNewSkill] = useState('');
  const [newSoftSkill, setNewSoftSkill] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [newWebsite, setNewWebsite] = useState("");
  const [newCert, setNewCert] = useState('');
  const [newTechnology, setNewTechnology] = useState("");

  const updateField = (field, data) => {
    onUpdate(prev => ({ ...prev, [field]: data }));
  };

  const handlePersonalChange = (field, value) => {
    onUpdate(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });
      
      if (response.ok) {
        const result = await response.json();
        handlePersonalChange('profile_image_url', result.file_url || URL.createObjectURL(file));
      } else {
        handlePersonalChange('profile_image_url', URL.createObjectURL(file));
      }
    } catch (error) {
      handlePersonalChange('profile_image_url', URL.createObjectURL(file));
    }
    setIsUploading(false);
  };

  const addSkill = (category) => {
    const skillValue = category === 'soft_skills' ? newSoftSkill : newSkill;
    if (skillValue.trim()) {
      const currentSkills = resumeData[category] || [];
      updateField(category, [...currentSkills, skillValue.trim()]);
      if (category === 'soft_skills') {
        setNewSoftSkill("");
      } else {
        setNewSkill("");
      }
    }
  };

  const removeSkill = (category, index) => {
    const currentSkills = resumeData[category] || [];
    updateField(category, currentSkills.filter((_, i) => i !== index));
  };
  
  const addEducation = () => {
    updateField("education", [...(resumeData.education || []), {
      degree: "",
      institution: "",
      start_date: "",
      end_date: "",
      description: ""
    }]);
  };

  const updateEducation = (index, field, value) => {
    const updated = [...(resumeData.education || [])];
    updated[index] = { ...updated[index], [field]: value };
    updateField("education", updated);
  };

  const removeEducation = (index) => {
    updateField("education", resumeData.education.filter((_, i) => i !== index));
  };

  const addLanguage = () => {
    updateField("languages", [...(resumeData.languages || []), ""]);
  };

  const updateLanguage = (index, value) => {
    const updated = [...(resumeData.languages || [])];
    updated[index] = value;
    updateField("languages", updated);
  };

  const removeLanguage = (index) => {
    updateField("languages", resumeData.languages.filter((_, i) => i !== index));
  };

  const addWebsite = () => {
    if (newWebsite.trim()) {
      updateField("websites", [...(resumeData.websites || []), newWebsite.trim()]);
      setNewWebsite("");
    }
  };

  const removeWebsite = (index) => {
    updateField("websites", resumeData.websites.filter((_, i) => i !== index));
  };

  const addProject = () => {
    const newProjectObj = { title: "", description: "", link: "", year: "", technologies: [] };
    updateField("projects", [...(resumeData.projects || []), newProjectObj]);
  };

  const removeProject = (index) => {
    updateField("projects", resumeData.projects.filter((_, i) => i !== index));
  };

  const updateProject = (index, field, value) => {
    const updated = [...(resumeData.projects || [])];
    updated[index] = { ...updated[index], [field]: value };
    
    // Maintain data consistency across templates
    if (field === 'technologies') {
      updated[index].tech_stack = Array.isArray(value) ? value : value.split(',').map(t => t.trim());
    }
    if (field === 'tech_stack') {
      updated[index].technologies = Array.isArray(value) ? value : value.split(',').map(t => t.trim());
    }
    if (field === 'link') {
      updated[index].github_link = value;
    }
    if (field === 'github_link') {
      updated[index].link = value;
    }
    
    updateField("projects", updated);
  };

  const addAchievement = () => {
    updateField("achievements", [...(resumeData.achievements || []), ""]);
  };

  const updateAchievement = (index, value) => {
    const updated = [...(resumeData.achievements || [])];
    updated[index] = value;
    updateField("achievements", updated);
  };

  const removeAchievement = (index) => {
    updateField("achievements", resumeData.achievements.filter((_, i) => i !== index));
  };

  const addCertification = () => {
    updateField("certifications", [...(resumeData.certifications || []), { name: "", provider: "" }]);
  };

  const updateCertification = (index, field, value) => {
    const updated = [...(resumeData.certifications || [])];
    updated[index] = { ...updated[index], [field]: value };
    updateField("certifications", updated);
  };

  const removeCertification = (index) => {
    updateField("certifications", resumeData.certifications.filter((_, i) => i !== index));
  };

  const addCert = () => {
    if (newCert.trim()) {
      updateField("certifications", [...(resumeData.certifications || []), newCert.trim()]);
      setNewCert("");
    }
  };

  const removeCert = (index) => {
    updateField("certifications", resumeData.certifications.filter((_, i) => i !== index));
  };

  const addTechnologyToProject = (projectIndex) => {
    if (newTechnology.trim()) {
      const updated = [...(resumeData.projects || [])];
      updated[projectIndex] = {
        ...updated[projectIndex],
        tech_stack: [...(updated[projectIndex].tech_stack || []), newTechnology.trim()]
      };
      updateField("projects", updated);
      setNewTechnology("");
    }
  };

  // Function to categorize skills from key_skills into structured format
  const categorizeSkills = (keySkills) => {
    if (!Array.isArray(keySkills)) return {};
    
    const categorized = {
      programming_languages: [],
      frameworks: [],
      tools: [],
      ai_ml_technologies: []
    };

    keySkills.forEach(skill => {
      const skillLower = skill.toLowerCase();
      if (skillLower.includes('python') || skillLower.includes('java') || skillLower.includes('javascript') || 
          skillLower.includes('c++') || skillLower.includes('sql') || skillLower.includes('html') || 
          skillLower.includes('css') || skillLower.includes('php') || skillLower.includes('ruby') ||
          skillLower.includes('go') || skillLower.includes('rust') || skillLower.includes('swift') ||
          skillLower.includes('c#') || skillLower.includes('kotlin') || skillLower.includes('scala')) {
        categorized.programming_languages.push(skill);
      } else if (skillLower.includes('react') || skillLower.includes('angular') || skillLower.includes('vue') ||
                 skillLower.includes('node') || skillLower.includes('django') || skillLower.includes('flask') ||
                 skillLower.includes('spring') || skillLower.includes('express') || skillLower.includes('laravel') ||
                 skillLower.includes('asp.net') || skillLower.includes('jquery') || skillLower.includes('bootstrap')) {
        categorized.frameworks.push(skill);
      } else if (skillLower.includes('git') || skillLower.includes('docker') || skillLower.includes('aws') ||
                 skillLower.includes('jenkins') || skillLower.includes('kubernetes') || skillLower.includes('jira') ||
                 skillLower.includes('agile') || skillLower.includes('scrum') || skillLower.includes('devops') ||
                 skillLower.includes('ci/cd') || skillLower.includes('terraform') || skillLower.includes('ansible')) {
        categorized.tools.push(skill);
      } else if (skillLower.includes('ai') || skillLower.includes('ml') || skillLower.includes('tensorflow') ||
                 skillLower.includes('pytorch') || skillLower.includes('scikit') || skillLower.includes('opencv') ||
                 skillLower.includes('deep learning') || skillLower.includes('neural networks') || skillLower.includes('nlp') ||
                 skillLower.includes('computer vision') || skillLower.includes('data science') || skillLower.includes('statistics')) {
        categorized.ai_ml_technologies.push(skill);
      } else {
        // For business/soft skills, add to tools category
        categorized.tools.push(skill);
      }
    });

    return categorized;
  };

  // Get skills data - prioritize structured skills, fallback to categorized key_skills
  // For templates, show empty skills. For uploaded resumes, show the skills data.
  const getSkillsData = () => {
    // If this is a template and no user data has been entered, return empty skills
    if (resumeData.isTemplate !== false && 
        (!resumeData.full_name || resumeData.full_name.trim() === '') &&
        (!resumeData.email || resumeData.email.trim() === '') &&
        (!resumeData.phone || resumeData.phone.trim() === '')) {
      return {
        programming_languages: [],
        frameworks: [],
        tools: [],
        ai_ml_technologies: []
      };
    }
    
    if (resumeData.skills && Object.keys(resumeData.skills).length > 0) {
      return resumeData.skills;
    }
    
    if (resumeData.key_skills && Array.isArray(resumeData.key_skills) && resumeData.key_skills.length > 0) {
      return categorizeSkills(resumeData.key_skills);
    }
    
    return {
      programming_languages: [],
      frameworks: [],
      tools: [],
      ai_ml_technologies: []
    };
  };

  // Auto-convert key_skills to structured skills when component mounts or resumeData changes
  // Only convert if this is NOT a template (i.e., user has uploaded a resume)
  useEffect(() => {
    if (resumeData.key_skills && Array.isArray(resumeData.key_skills) && resumeData.key_skills.length > 0 && 
        (!resumeData.skills || Object.keys(resumeData.skills).length === 0) &&
        resumeData.isTemplate === false) {
      const categorizedSkills = categorizeSkills(resumeData.key_skills);
      updateField('skills', categorizedSkills);
    }
  }, [resumeData.key_skills, resumeData.skills, resumeData.isTemplate]);

  const baseSections = [
    { id: 'contact', label: 'Contact' },
    { id: 'summary', label: 'Summary' },
    { id: 'experience', label: 'Experience' },
    { id: 'skills', label: 'Skills' },
    { id: 'education', label: 'Education' },
    { id: 'achievements', label: 'Achievements' },
    { id: 'certifications', label: 'Certifications' },
    // { id: 'languages', label: 'Languages' }, // Removed - comes from dynamic sections
    { id: 'projects', label: 'Projects' }
  ];

  // Combine base sections with dynamic sections from ResumeEditor
  const sections = getDynamicSections ? [...baseSections, ...getDynamicSections()] : baseSections;

  return (
    <div className="resume-form" style={{ marginTop: 0, paddingTop: 0 }}>
      <div className="form-content" style={{ marginTop: 0, paddingTop: 0 }}>
      <div className="form-header">
          <h2 className="form-title">Resume Editor</h2>
      </div>
        <SectionSelector
          sections={sections}
          activeSection={activeSection}
          onSectionChange={setActiveSection}
        />
        <div className="section-form">
        {activeSection === 'contact' && (
          <div className="tab-panel">
              <h3 className="section-title">Contact Information</h3>
            <div className="form-group-grid">

                <div className={`form-group ${validationErrors.full_name ? 'error' : ''}`}>
                  <label>Full Name</label>
                <input
                  type="text"
                    value={resumeData?.full_name || ""}
                  onChange={(e) => handlePersonalChange("full_name", e.target.value)}
                    onFocus={() => onFieldFocus && onFieldFocus("full_name")}
                    onBlur={() => onFieldBlur && onFieldBlur("full_name")}
                    placeholder="Ryan Monroe"
                />
                {validationErrors.full_name && <p className="error-message">{validationErrors.full_name}</p>}
              </div>

                <div className={`form-group ${validationErrors.job_title ? 'error' : ''}`}>
                  <label>Job Title</label>
                  <input
                    type="text"
                    value={resumeData?.job_title || ""}
                    onChange={(e) => handlePersonalChange("job_title", e.target.value)}
                    onFocus={() => onFieldFocus && onFieldFocus("job_title")}
                    onBlur={() => onFieldBlur && onFieldBlur("job_title")}
                    placeholder={resumeData?.isTemplate ? "High School Computer Science Teacher" : "Enter your job title"}
                  />
                  {validationErrors.job_title && <p className="error-message">{validationErrors.job_title}</p>}
                </div>

                <div className={`form-group ${validationErrors.email ? 'error' : ''}`}>
                  <label>Email Address</label>
                <input
                  type="email"
                    value={resumeData?.email || ""}
                  onChange={(e) => handlePersonalChange("email", e.target.value)}
                    onFocus={() => onFieldFocus && onFieldFocus("email")}
                    onBlur={() => onFieldBlur && onFieldBlur("email")}
                    placeholder="ryanmonroe47@gmail.com"
                    maxLength="60"
                />
                {validationErrors.email && <p className="error-message">{validationErrors.email}</p>}
              </div>

                <div className={`form-group ${validationErrors.phone ? 'error' : ''}`}>
                  <label>Phone Number</label>
                <input
                    type="text"
                    value={resumeData?.phone || ""}
                  onChange={(e) => handlePersonalChange("phone", e.target.value)}
                    onFocus={() => onFieldFocus && onFieldFocus("phone")}
                    onBlur={() => onFieldBlur && onFieldBlur("phone")}
                    placeholder="+1 (302) 559-2291"
                  maxLength="20"
                />
                {validationErrors.phone && <p className="error-message">{validationErrors.phone}</p>}
              </div>

                <div className={`form-group ${validationErrors.location ? 'error' : ''}`}>
                <label>Location</label>
                <input
                  type="text"
                    value={resumeData?.location || ""}
                  onChange={(e) => handlePersonalChange("location", e.target.value)}
                    onFocus={() => onFieldFocus && onFieldFocus("location")}
                    onBlur={() => onFieldBlur && onFieldBlur("location")}
                    placeholder="Wilmington, DE"
                    maxLength="50"
                  />
                  {validationErrors.location && <p className="error-message">{validationErrors.location}</p>}
                </div>

                <div className={`form-group`}>
                  <label>GitHub</label>
                  <input
                    type="text"
                    value={resumeData?.github || ""}
                    onChange={(e) => handlePersonalChange("github", e.target.value)}
                    placeholder="https://github.com/yourusername"
                  />
                </div>

                <div className={`form-group`}>
                  <label>LinkedIn</label>
                  <input
                    type="text"
                    value={resumeData?.linkedin || ""}
                    onChange={(e) => handlePersonalChange("linkedin", e.target.value)}
                    placeholder="https://linkedin.com/in/yourprofile"
                  />
                </div>

                <div className={`form-group`}>
                  <label>LeetCode</label>
                  <input
                    type="text"
                    value={resumeData?.leetcode || ""}
                    onChange={(e) => handlePersonalChange("leetcode", e.target.value)}
                    placeholder="https://leetcode.com/yourusername"
                  />
                </div>

                <div className={`form-group`}>
                  <label>HackerRank</label>
                  <input
                    type="text"
                    value={resumeData?.hackerrank || ""}
                    onChange={(e) => handlePersonalChange("hackerrank", e.target.value)}
                    placeholder="https://www.hackerrank.com/yourusername"
                  />
                </div>

              </div>

              <div className="form-group">
                <label>Personal Website</label>
                <input
                  type="url"
                  value={resumeData.personal_website || ""}
                  onChange={(e) => handlePersonalChange("personal_website", e.target.value)}
                  placeholder="https://johndoe.com"
                  className="form-input"
                  maxLength="200"
                />
              </div>

              <div className="form-group">
                <label>Portfolio</label>
                <input
                  type="url"
                  value={resumeData.portfolio || ""}
                  onChange={(e) => handlePersonalChange("portfolio", e.target.value)}
                  placeholder="https://portfolio.johndoe.com"
                  className="form-input"
                  maxLength="200"
                />
              </div>

              <WebsiteLinksInput
                websites={resumeData.websites || []}
                newWebsite={newWebsite}
                setNewWebsite={setNewWebsite}
                onAdd={addWebsite}
                onRemove={removeWebsite}
                label="Additional Websites/Links"
              />

            <div className="form-group">
              <label>Profile Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={isUploading}
              />
              {isUploading && <p>Uploading...</p>}
              {resumeData.profile_image_url && (
                <div className="image-preview" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
                  <img src={resumeData.profile_image_url} alt="Profile" style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px' }} />
                  <RemoveButton
                    onClick={() => handlePersonalChange('profile_image_url', '')}
                    ariaLabel="Remove profile image"
                  >
                    🗑️ Remove
                  </RemoveButton>
                </div>
              )}
            </div>
          </div>
        )}

        {activeSection === 'summary' && (
          <div className="tab-panel">
              <h3 className="section-title">Professional Summary</h3>
            <div className="form-group">
                <label>Professional Summary</label>
              <textarea
                  value={resumeData?.professional_summary || ""}
                onChange={(e) => handlePersonalChange("professional_summary", e.target.value)}
                  onFocus={() => onFieldFocus && onFieldFocus("professional_summary")}
                  onBlur={() => onFieldBlur && onFieldBlur("professional_summary")}
                  placeholder="Experienced software developer with 5+ years of expertise in full-stack development, specializing in React, Node.js, and cloud technologies. Proven track record of delivering scalable solutions and leading cross-functional teams..."
                  rows={6}
                  maxLength="1000"
              />
              {validationErrors.professional_summary && <p className="error-message">{validationErrors.professional_summary}</p>}
            </div>
          </div>
        )}

        {activeSection === 'experience' && (
          <div className="tab-panel">
            <ExperiencesInput
              experiences={resumeData.relevant_experience}
              onChange={(updated) => updateField("relevant_experience", updated)}
              errors={validationErrors.relevant_experience}
              onFieldFocus={onFieldFocus}
              onFieldBlur={onFieldBlur}
              title="Relevant Experience"
              addLabel="Add Experience"
            />
          </div>
        )}

        {activeSection === 'skills' && (
          <div className="tab-panel">
            <div className="section-header">
              <h3 className="section-title">Skills</h3>
            </div>

            <CategorizedSkillsInput
              skills={getSkillsData()}
              onClear={() => {
                const clearedSkills = {
                  programming_languages: [],
                  frameworks: [],
                  tools: [],
                  ai_ml_technologies: [],
                };
                onUpdate((prev) => ({
                  ...prev,
                  skills: clearedSkills,
                  ...(prev.key_skills ? { key_skills: [] } : {}),
                }));
              }}
              clearLabel="Clear"
              onChange={(updatedSkills) => {
                onUpdate((prev) => ({
                  ...prev,
                  skills: updatedSkills,
                  ...(prev.key_skills ? { key_skills: [] } : {}),
                }));
              }}
              categories={[
                { key: 'programming_languages', label: 'Programming Languages', placeholder: 'Add a programming language...' },
                { key: 'frameworks', label: 'Frameworks', placeholder: 'Add a framework...' },
                { key: 'tools', label: 'Tools', placeholder: 'Add a tool...' },
                { key: 'ai_ml_technologies', label: 'AI/ML Technologies', placeholder: 'Add an AI/ML technology...' },
              ]}
            />
          </div>
        )}

        {activeSection === 'education' && (
          <div className="tab-panel">
            <div className="section-header">
              <h3 className="section-title">Education</h3>
              <AddButton onClick={addEducation}>+ Add Education</AddButton>
            </div>
            {(resumeData.education || []).map((edu, index) => {
              const eduErrors = validationErrors.education?.[index] || {};
              return (
                <div key={index} className="education-card">
                  <div className="card-header">
                    <h4>Education {index + 1}</h4>
                      <RemoveButton onClick={() => removeEducation(index)} ariaLabel={`Remove education ${index + 1}`} />
                  </div>
                  <div className="card-content">

                      <div className="form-group">
                      <label>Degree</label>
                        <input 
                          type="text"
                          value={edu.degree || ""}
                          onChange={(e) => updateEducation(index, "degree", e.target.value)}
                          placeholder="Bachelor of Technology"
                        />
                    </div>

                      <div className="form-group">
                        <label>Field of Study</label>
                        <input 
                          type="text"
                          value={edu.field_of_study || ""}
                          onChange={(e) => updateEducation(index, "field_of_study", e.target.value)}
                          placeholder="Electronics and Communication Engineering"
                        />
                    </div>

                      <div className="form-group">
                        <label>Institute</label>
                        <input 
                          type="text"
                          value={edu.institute || ""}
                          onChange={(e) => updateEducation(index, "institute", e.target.value)}
                          placeholder="Siddhartha Institute of Technology and Sciences"
                        />
                      </div>

                      <div className="form-group">
                        <label>Location</label>
                        <input 
                          type="text"
                          value={edu.location || ""}
                          onChange={(e) => updateEducation(index, "location", e.target.value)}
                          placeholder="Hyderabad, Telangana"
                        />
                      </div>

                    <div className="form-group-grid">
                        <div className="form-group">
                          <label>Start Year</label>
                          <input 
                            type="text"
                            value={edu.start_year || ""}
                            onChange={(e) => updateEducation(index, "start_year", e.target.value)}
                            placeholder="2020"
                          />
                      </div>
                        <div className="form-group">
                          <label>End Year</label>
                          <input 
                            type="text"
                            value={edu.end_year || ""}
                            onChange={(e) => updateEducation(index, "end_year", e.target.value)}
                            placeholder="2024"
                          />
                      </div>
                    </div>

                      <div className="form-group">
                        <label>CGPA</label>
                        <input 
                          type="text"
                          value={edu.cgpa || ""}
                          onChange={(e) => updateEducation(index, "cgpa", e.target.value)}
                          placeholder="7.09/10"
                        />
                    </div>

                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Languages section removed - now handled by dynamic sections */}

        {activeSection === 'achievements' && (
          <div className="tab-panel">
            <div className="section-header">
              <h3 className="section-title">Achievements</h3>
                <AddButton onClick={() => updateField("achievements", [...(resumeData.achievements || []), ""])}>
                + Add Achievement
              </AddButton>
            </div>
              {(resumeData.achievements || []).map((item, index) => (
                <div key={index} className="form-group" style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => {
                      const updated = [...resumeData.achievements];
                      updated[index] = e.target.value;
                      updateField("achievements", updated);
                    }}
                    placeholder="E.g., GATE Qualified - 97.43 percentile"
                  />
                  <button onClick={() => {
                    const updated = resumeData.achievements.filter((_, i) => i !== index);
                    updateField("achievements", updated);
                  }}>🗑️</button>
              </div>
            ))}
          </div>
        )}

        {activeSection === 'certifications' && (
          <div className="tab-panel">
            <div className="section-header">
              <h3 className="section-title">Certifications</h3>
            </div>
                    <div className="form-group">
              <label>Add Certification</label>
              <div className="add-skill-container">
                      <input
                        type="text"
                  value={newCert}
                  onChange={(e) => setNewCert(e.target.value)}
                  placeholder="e.g. AWS Certified Solutions Architect"
                        maxLength="100"
                      />
                <button onClick={addCert} className="add-skill-btn">Add</button>
                    </div>
                    </div>
            <div className="skills-list">
              {(resumeData.certifications || []).map((cert, index) => (
                <span key={index} className="skill-tag">
                  <span>
                    {typeof cert === 'string' ? cert : (cert.name || cert)}
                    {typeof cert === 'object' && cert.provider ? ` – ${cert.provider}` : ""}
                  </span>
                  <button onClick={() => removeCert(index)} className="remove-skill-btn">×</button>
                </span>
            ))}
          </div>
            {validationErrors.certifications && <p className="error-message">{validationErrors.certifications}</p>}
          </div>
        )}


        {activeSection === 'projects' && (
          <div className="tab-panel">
            <div className="section-header">
              <h3 className="section-title">Projects</h3>
                <AddButton onClick={addProject}>+ Add Project</AddButton>
            </div>
            {(resumeData.projects || []).map((proj, index) => (
                <div key={index} className="project-card">
                <div className="card-header">
                  <h4>Project {index + 1}</h4>
                    <RemoveButton onClick={() => removeProject(index)} ariaLabel={`Remove project ${index + 1}`} />
                </div>
                <div className="card-content">
                  <div className="form-group">
                      <label>Project Title</label>
                    <input
                      type="text"
                      value={proj.title || ""}
                      onChange={(e) => updateProject(index, "title", e.target.value)}
                        placeholder="E-commerce Website"
                    />
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={proj.description || ""}
                      onChange={(e) => updateProject(index, "description", e.target.value)}
                      placeholder="Describe your project..."
                      rows={3}
                        maxLength="500"
                    />
                  </div>
                    <div className="form-group-grid">
                  <div className="form-group">
                    <label>Link</label>
                    <input
                      type="text"
                          value={proj.link || proj.github_link || ''}
                      onChange={(e) => updateProject(index, "link", e.target.value)}
                      placeholder="https://github.com/your/project"
                    />
                  </div>
                  <div className="form-group">
                    <label>Year</label>
                    <input
                      type="text"
                      value={proj.year || ""}
                      onChange={(e) => updateProject(index, "year", e.target.value)}
                      placeholder="2025"
                    />
                      </div>
                  </div>
                  <div className="form-group">
                      <label>Technologies</label>
                      <div className="add-skill">
                    <input
                      type="text"
                          value={newTechnology}
                          onChange={(e) => setNewTechnology(e.target.value)}
                          placeholder="Add technology"
                          maxLength="50"
                        />
                        <button onClick={() => addTechnologyToProject(index)}>Add</button>
                      </div>
                      <div className="skills-list">
                        {(proj.technologies || []).map((tech, techIndex) => (
                          <span key={techIndex} className="skill-tag">
                            {tech}
                            <button onClick={() => {
                              const updatedProjects = [...(resumeData.projects || [])];
                              updatedProjects[index].technologies = updatedProjects[index].technologies.filter((_, i) => i !== techIndex);
                              updateField("projects", updatedProjects);
                            }}>×</button>
                          </span>
                        ))}
                      </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* DYNAMIC SECTIONS - Rendered from ResumeEditor.jsx */}
        {renderDynamicSectionForm && getDynamicSections && getDynamicSections().map(section => {
          if (activeSection === section.id && !baseSections.find(s => s.id === section.id)) {
            return renderDynamicSectionForm(section.id);
          }
          return null;
        })}

        </div>
      </div>
    </div>
  );
};

export default TechResumeForm;


